import { CXoneAcdClient } from '@nice-devone/acd-sdk';
import { CXonePopUrl } from '@nice-devone/common-sdk';
import { LoggerService } from 'src/app/core/services/logger.service';
import { CxOneUtils } from '../util/CxOneUtils';
import { Subject } from 'rxjs';
import { Injectable } from '@angular/core';

export interface PopUrlInitOptions {
  runApp: boolean;
  pageOpen: boolean;
  contactScreenPop: boolean;
  popUrl: boolean;
  agentAssist: boolean;
  customScreenpop: boolean;
}

interface PopUrlData {
  u_json?: {
    TIMEOUTTEMPO?: number;
    TIMEOUTATIVAMAISTEMPO?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface PopUrl {
  popUrl: CXonePopUrl;
  data: PopUrlData;
}

const DATA_EVENT = 'ATTACHESDYNAMICS';
const FOCUS_EVENT = 'FOCUS';

@Injectable({
  providedIn: 'root',
})
export class PopUrlService {
  private readonly receivedPopUrls = new Map<string, CXonePopUrl>();
  public readonly onReceiveFocus$ = new Subject<CXonePopUrl>();
  public readonly onReceiveData$ = new Subject<PopUrl>();

  public constructor(private logger: LoggerService) {}

  public initialize(
    client?: CXoneAcdClient,
    options?: PopUrlInitOptions,
  ): void {
    if (!client) client = CXoneAcdClient.instance;

    if (!options)
      options = {
        runApp: true,
        pageOpen: true,
        contactScreenPop: true,
        popUrl: true,
        agentAssist: true,
        customScreenpop: true,
      };

    client.screenPop.screenPopSubscribe(
      options.runApp,
      options.pageOpen,
      options.contactScreenPop,
      options.popUrl,
      options.agentAssist,
      options.customScreenpop,
    );

    client.screenPop.popUrlSubject.subscribe(async (popUrlData) => {
      this.logger.info('CXonePopUrl - Recebido: ', popUrlData);
      const eventName = popUrlData.tabTitle?.toUpperCase();

      if (eventName === FOCUS_EVENT) {
        this.onReceiveFocus$.next(popUrlData);
        return;
      }

      if (eventName === DATA_EVENT) {
        this.receivedPopUrls.set(popUrlData.contactId, popUrlData);
        this.onReceiveData$.next(PopUrlService.parsePopUrlData(popUrlData)!);
      }
    });
  }

  public async getPopUrl(
    contactId: string,
    timeout = 10,
  ): Promise<PopUrl | null> {
    this.logger.info('CXonePopUrl - Iniciando busca pelo evento');

    for (let tentativa = 1; tentativa <= timeout; tentativa++) {
      this.logger.info(`CXonePopUrl - Tentativa ${tentativa} de ${timeout}.`);

      const popUrl = this.receivedPopUrls.get(contactId);

      if (popUrl) {
        this.logger.info('CXonePopUrl - encontrado:', contactId);
        return PopUrlService.parsePopUrlData(popUrl);
      }

      this.logger.warn('CXonePopUrl - não encontrado:', contactId);

      await CxOneUtils.Delay(1000);
    }

    return null;
  }

  public static deserializeScreenPopUrl(screenPopUrl: string) {
    const objeto: Record<string, unknown> = {};

    screenPopUrl = screenPopUrl.replaceAll("'", '"');

    const pares = screenPopUrl.split('|');

    pares.forEach((par) => {
      const idx = par.indexOf(':');
      if (idx <= -1) return;

      const key = par.substring(0, idx);
      if (!key) return;

      const value = par.substring(idx + 1);

      try {
        objeto[key.toLowerCase()] = JSON.parse(value);
      } catch {
        objeto[key.toLowerCase()] = value;
      }
    });

    return objeto;
  }

  private static parsePopUrlData(popUrlData: CXonePopUrl): PopUrl | null {
    if (!popUrlData) return null;
    const data = PopUrlService.deserializeScreenPopUrl(popUrlData.url) ?? {};
    return {
      popUrl: popUrlData,
      data: data as PopUrlData,
    };
  }

  public static parsePopUrl(
    screenPopUrl: unknown,
  ): Record<string, unknown> | null {
    const type = typeof screenPopUrl;
    if (type === 'object') return screenPopUrl as Record<string, unknown>;
    if (type !== 'string') return null;

    try {
      return JSON.parse(screenPopUrl as string);
    } catch {
      return PopUrlService.deserializeScreenPopUrl(screenPopUrl as string);
    }
  }
}
